/**
 * Pure routing/decision logic for the post-consent landing screen (issue
 * #23952) — factored out of `main.tsx`'s `AuthGate` for the same reason
 * `auth_gate_screen.ts` was: this is a discriminated-string decision over
 * several orthogonal inputs (session kind, payroll role, admin bit,
 * organisation-status result), and getting the ordering wrong is invisible to
 * TypeScript. A pure function + `bun:test` unit tests makes a regression a
 * one-line assertion away from being caught.
 *
 * Sits AFTER {@link resolveAuthGateScreen} returns `'app'` — every branch
 * below therefore already has an authenticated session with resolved facts
 * (or `sessionKind: 'employee'`, which uses the boot's own facts). It never
 * duplicates the pre-`'app'` decisions above it (grant_error/session_error/
 * no_entity/…).
 *
 * Rules (from #23952):
 *
 * - Employee session → `employee_my_pay` (the existing My Pay surface, plus a
 *   small "You're connected to <Org> as an employee" note).
 * - Any employee-only runtime (`employeeViewOnly` — today: the native
 *   Capacitor shell, `apps/wagepro-mobile`) → `dashboard`, i.e. straight into
 *   `<App />`, which renders the employee surface there. Every landing below
 *   this line is an EMPLOYER screen (first-run setup, payroll-inactive, the
 *   employer-scoped status loading/error surfaces) and must not appear on a
 *   phone. See `data/employee_view_only.ts`.
 * - Employer session, non-admin firm member (Ledger role not "owner"/"admin"
 *   and entity role not "admin") → `no_payroll_role` — the "Your Ledger role
 *   on <Org> doesn't include payroll" fallback that replaces the old bare-dead-end
 *   `NoPayrollAccessScreen`.
 * - Employer session, admin, organisation status `loading` → `loading` (render
 *   a placeholder, don't flash the first-run screen while the query is in-flight).
 * - Employer session, admin, organisation status `error` → `error` (retry surface).
 * - Employer session, admin, `connected && !has_payroll_entitlement` →
 *   `payroll_inactive` (the payroll add-on is revoked/expired/never granted —
 *   `connectOrganisation` will refuse to re-grant, so the previous "route to
 *   first_run_setup, click Set up, hope it works" flow spun in a false-toast
 *   loop; the HIGH-severity adversarial-review finding). This landing has
 *   NO connect CTA — only "Back to Ledger" and Sign out. Fix path is
 *   Ledger-side, so escalating there is the only real remedy.
 * - Employer session, admin, `connected && has_payroll_entitlement` →
 *   `dashboard` (drop them onto the real app).
 * - Employer session, admin, `!connected` → `first_run_setup` (the
 *   "Set up payroll for <Org>" screen — WagePro has no company row yet).
 */

export type ConnectLanding =
  | 'dashboard'
  | 'first_run_setup'
  | 'payroll_inactive'
  | 'employee_my_pay'
  | 'no_payroll_role'
  | 'loading'
  | 'error';

/**
 * The organisation-status projection this decision cares about. Kept
 * deliberately narrow — the exact `wageproAuth.organisationStatus` response
 * shape is one place away (`data/use_organisation_status.ts`), and this
 * function only reads booleans off it. Keeping the pure decision function
 * decoupled from the transport type also lets the tests express states
 * without constructing a fake tRPC response.
 */
export type OrganisationStatusResult =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      connected: boolean;
      has_payroll_entitlement: boolean;
    };

export interface ConnectLandingInput {
  /**
   * Session shape from `auth_context.tsx` — an employee session never resolves
   * employer facts and its landing is a fixed decision. See
   * {@link resolveAuthGateScreen}'s own `sessionKind === 'employee'` branch
   * docstring for why an employee's `useSessionFacts()` reports `no_entity`
   * (deliberately) and the boot supplies its facts instead.
   */
  sessionKind: 'employer' | 'employee';
  /**
   * `entity_memberships.payroll_role` via `entityUser.me` — the same field
   * `SessionFacts.payroll_role` in `wagepro_store_session_facts.ts` carries.
   * `'none'` is the fallback branch every REAL non-adviser membership resolves
   * to today (see `deriveRole`'s docblock).
   */
  payrollRole: 'none' | 'employee' | 'admin';
  /**
   * "Is this caller allowed to run `wageproAuth.connectOrganisation` on this
   * entity?" — mirrors the SAME rule `wageproAuth.ts` (~L1030) enforces
   * server-side (firm owner/admin OR entity admin). Pass this via
   * {@link deriveIsAdmin} so the two stay in lock-step; a mismatch is
   * exactly the HIGH-severity bug the adversarial review of PR #23958
   * caught (`payroll_role`/`is_adviser`/`manage_users`-derived "admin"
   * flagged every firm staffer including readonly, showing them a connect
   * CTA that then 403'd).
   */
  isAdmin: boolean;
  /**
   * `true` when this runtime can only ever show the employee surface — the
   * native Capacitor shell (`data/employee_view_only.ts`'s
   * `resolveEmployeeViewOnly`, surfaced by `use_session.ts` as
   * `is_employee_view_only`).
   *
   * Required, not optional: this decision function's whole reason for
   * existing is that its ordering is invisible to TypeScript, so a new call
   * site is made to state the answer rather than inherit a default.
   */
  employeeViewOnly: boolean;
  organisationStatus: OrganisationStatusResult;
}

/**
 * Server-parity `isAdmin` derivation. Mirrors the exact server-side rule
 * `wageproAuth.connectOrganisation` enforces (`apps/api/src/trpc/routers/
 * wagepro_auth.ts` ~L1030):
 *
 *   `ctx.firmRole === 'owner' || ctx.firmRole === 'admin' || <entity admin>`
 *
 * where entity-admin means `ctx.entityPerms.role === 'admin'` (which
 * `requireEntityAdmin` checks).
 *
 * The pre-fixup logic (`payroll_role === 'admin' || is_adviser ||
 * manage_users`) drifted from this rule in TWO directions at once:
 *
 *  1. `is_adviser` is true for EVERY firm staffer (`entityUser.me` sets
 *     `is_adviser` from `accessSource !== 'explicit_client'`), so a
 *     read-only firm member was flagged as admin-like.
 *  2. `manage_users` reflects the `settings.manage_users_and_permissions`
 *     grant, which a `standard` firm role also carries by default.
 *
 * The result: the connect CTA showed for accounts the server would then
 * reject with FORBIDDEN. This helper anchors both sides of that check on
 * the same field names so a future edit either changes both or fails a
 * test.
 */
export function deriveIsAdmin(input: {
  firmRole: string;
  entityRole: string;
}): boolean {
  return (
    input.firmRole === 'owner' ||
    input.firmRole === 'admin' ||
    input.entityRole === 'admin'
  );
}

/**
 * Whether {@link useOrganisationStatus} should actually fire its query.
 *
 * Skips it for callers who can never see the setup screen (employees and
 * non-admin firm members) — {@link resolveConnectLanding} decides their
 * landing without touching organisation status, so paying for the round
 * trip is wasted. This is what the `enabled` prop on
 * {@link useOrganisationStatus} consumes.
 */
export function shouldQueryOrganisationStatus(input: {
  sessionKind: 'employer' | 'employee';
  isAdmin: boolean;
  employeeViewOnly: boolean;
}): boolean {
  if (input.sessionKind === 'employee') return false;
  // An employee-only runtime (the native shell) never renders any of the
  // screens this status discriminates, so the round trip buys nothing there
  // either — same reasoning as the employee branch above.
  if (input.employeeViewOnly) return false;
  return input.isAdmin;
}

/**
 * Pure decision. All rules above encoded literally, in the order they are
 * documented. `sessionKind === 'employee'` wins first because it means we
 * never even asked for organisation status — employees have no employer
 * facts. Then the "no payroll role at all" case fires BEFORE reading
 * organisation status, because that decision is independent of whether the
 * organisation is set up (a non-payroll firm member cannot do anything on
 * either the setup screen or the dashboard).
 *
 * `payroll_inactive` fires AFTER organisation-status resolves, gated on
 * `connected && !has_payroll_entitlement`. The distinction from
 * `first_run_setup` matters: an "unconnected" org needs the admin to click
 * "Set up payroll" (which mints the wagepro_companies row); a "connected
 * but no entitlement" org has already been through that once — the add-on
 * is missing (revoked, expired, or never granted at all). Sending them to
 * the first-run screen was the pre-fixup behaviour and caused the HIGH-
 * severity loop the adversarial review of PR #23958 caught: `connectOrganisation`
 * correctly refuses to re-grant, the toast fires anyway, and the status
 * refetch lands on the same screen forever.
 */
export function resolveConnectLanding(
  input: ConnectLandingInput,
): ConnectLanding {
  if (input.sessionKind === 'employee') {
    return 'employee_my_pay';
  }

  if (!input.isAdmin && input.payrollRole !== 'employee') {
    return 'no_payroll_role';
  }

  // Employee-only runtime (the native shell): hand straight over to `<App />`,
  // which renders the employee surface. Deliberately AFTER `no_payroll_role`
  // — that screen is an explanation of the caller's own Ledger role, not an
  // employer tool, and swallowing it here would replace a clear "your role
  // doesn't include payroll" message with an empty My Pay screen. It is
  // deliberately BEFORE every organisation-status branch below, all of which
  // lead to employer-only screens (first-run setup, payroll-inactive, and the
  // loading/error surfaces that exist only to gate them).
  if (input.employeeViewOnly) {
    return 'dashboard';
  }

  // An employee-role membership on an employer session (rare — an admin who
  // is also an employee on the same entity) still gets the dashboard rather
  // than the first-run screen. The dashboard's own action-needed card
  // surfaces any setup work.
  if (!input.isAdmin) {
    return 'dashboard';
  }

  if (input.organisationStatus.status === 'loading') {
    return 'loading';
  }
  if (input.organisationStatus.status === 'error') {
    return 'error';
  }

  if (
    input.organisationStatus.connected &&
    input.organisationStatus.has_payroll_entitlement
  ) {
    return 'dashboard';
  }
  if (
    input.organisationStatus.connected &&
    !input.organisationStatus.has_payroll_entitlement
  ) {
    return 'payroll_inactive';
  }

  return 'first_run_setup';
}

/**
 * Pure decision for whether {@link ConnectLandingGate}'s post-connect toast
 * should fire — pulled out of the component so a regression here is a
 * one-line assertion away from being caught.
 *
 * Called AFTER `wageproAuth.connectOrganisation` succeeds AND the caller
 * has re-fetched `organisationStatus` (`useOrganisationStatus.retry()`).
 * The refetched result is what discriminates a real activation from the
 * FALSE-POSITIVE cases the pre-fixup code (and the first fixup pass) both
 * shipped:
 *
 * - `ready + connected + has_payroll_entitlement` — real activation, toast fires.
 * - `ready + connected + !has_payroll_entitlement` — the payroll add-on is
 *   revoked/expired/never granted: the `payroll_inactive` screen will
 *   render, so NO toast (celebrating the "connection" when the user has
 *   just been shown a dead-end is what the second adversarial review flagged).
 * - `ready + !connected` — the mutation nominally succeeded but the
 *   organisation is still reported as not connected (a transient consistency
 *   race, or an unusual server response). No toast — nothing celebratory
 *   to say, and firing here would misreport reality.
 * - `loading` / `error` — the refetch is still in flight or failed; no
 *   toast yet. The caller keeps the pending marker set and re-invokes this
 *   helper on the next state change.
 *
 * Discriminated return: `{ decide: 'fire' }` fires the toast and clears the
 * pending marker; `{ decide: 'suppress' }` clears the pending marker
 * without firing; `{ decide: 'wait' }` keeps the pending marker so the next
 * status transition is evaluated.
 */
export type PostConnectToastDecision =
  | { decide: 'fire' }
  | { decide: 'suppress' }
  | { decide: 'wait' };

export function resolvePostConnectToast(
  refetchedStatus: OrganisationStatusResult,
): PostConnectToastDecision {
  if (refetchedStatus.status !== 'ready') return { decide: 'wait' };
  if (refetchedStatus.connected && refetchedStatus.has_payroll_entitlement) {
    return { decide: 'fire' };
  }
  return { decide: 'suppress' };
}
